import { apiGet, apiPost } from "@softmax/observatory-client/client";
import {
  DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
  type ApiTransport,
} from "@softmax/observatory-client/transport";
import { withRequestDeadline } from "@softmax/observatory-client/request-signal";

import { watchableLeagues } from "@softmax/web-features/leagues";

import { navigationLeagues } from "@softmax/web-features/site/catalog";
import { publicWatchApi } from "@softmax/web-features/data/public-api";
import { type WatchTheaterSeed } from "@/lib/leagues/watch-types";
import { loadWatchLeagueState } from "@/lib/leagues/watch";
import { slimLeagueForTheater } from "@/lib/leagues/watch-seed-slim";
import { listedPublic } from "@softmax/web-features/coworlds";
import {
  type WatchSeedAddress,
  storedWatchLeague,
  watchLeagueFor,
} from "./requests/watch-address.data";
import { resolveSelectedWatchLeague } from "./requests/selected-league.data";

async function seedLeagues({
  signal,
  selectedId,
  withNavigation,
}: {
  readonly signal: AbortSignal | undefined;
  readonly selectedId: string | null;
  readonly withNavigation: boolean;
}): Promise<Pick<WatchTheaterSeed, "leagues" | "viewer">> {
  const path = selectedId
    ? `/v2/leagues/${encodeURIComponent(selectedId)}`
    : null;
  const resolved = await resolveSelectedWatchLeague({ signal, path });
  const selection = listedPublic(
    resolved.selected ? watchableLeagues([resolved.selected]) : [],
  );
  const navigation =
    !withNavigation && selection.length > 0
      ? []
      : await navigationLeagues({ signal });
  return {
    leagues: listedPublic([
      ...selection,
      ...navigation.filter((league) => league.id !== selection[0]?.id),
    ]),
    viewer: selection.length > 0 ? resolved.viewer : null,
  };
}

export async function watchSeed({
  signal,
  address,
  withState = true,
  withNavigation = true,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly address: WatchSeedAddress;
  readonly withState?: boolean;
  readonly withNavigation?: boolean;
}): Promise<WatchTheaterSeed> {
  const generatedAt = Date.now();
  const { stored = storedWatchLeague() } = address;
  let leagues = address.corpus;
  let viewer = address.viewer ?? null;
  const hideBrowseHidden = address.corpus === undefined;

  if (leagues === undefined) {
    const selection = await seedLeagues({
      signal,
      selectedId: address.leagueId ?? stored,
      withNavigation,
    });
    leagues = selection.leagues;
    viewer = selection.viewer;
  }

  if (!leagues)
    return {
      generatedAt,
      leagues: null,
      state: null,
      stored,
      viewer,
      hideBrowseHidden,
    };

  const seed: WatchTheaterSeed = {
    generatedAt,
    leagues: leagues.map(slimLeagueForTheater),
    state: null,
    stored,
    viewer,
    hideBrowseHidden,
  };

  if (!withState) return seed;

  const league = await watchLeagueFor({
    signal,
    address: { ...address, corpus: leagues },
  });

  if (!league) return seed;

  const requestSignal = withRequestDeadline(
    signal,
    DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
  );
  const api: ApiTransport = viewer
    ? {
        get: <T>(path: string) =>
          apiGet<T>(path, { elevatedPrivileges: false, signal: requestSignal }),
        post: <T>(path: string, body?: unknown) =>
          apiPost<T>(path, body, {
            elevatedPrivileges: false,
            signal: requestSignal,
          }),
      }
    : publicWatchApi({ signal: requestSignal });
  return {
    ...seed,
    state: await loadWatchLeagueState({ api, league, wave: "feature" }),
  };
}
